import { useId, useMemo, useState, type KeyboardEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import { Group, Pill, Button } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';

/**
 * W9: tag chips on the conversation header. Add opens an inline input with
 * autocomplete over the org's existing tags (most-used first); Enter commits
 * the typed text, ↑/↓ + Enter (or Tab) pick a suggestion, Esc closes.
 */
export function TagChips({ conversationId, addClassName }: { conversationId: string; addClassName?: string }) {
  const t = useT();
  const qc = useQueryClient();
  const listId = useId();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [active, setActive] = useState(-1);

  const tags = useQuery({
    queryKey: ['conversation', conversationId, 'tags'],
    queryFn: () => inbox.listTags(conversationId)
  });
  const orgTags = useQuery({
    queryKey: ['org-tags'],
    queryFn: () => inbox.listOrgTags({ limit: 200 }),
    enabled: adding,
    staleTime: 60_000
  });

  const current = tags.data?.data ?? [];
  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const have = new Set(current.map((x) => x.toLowerCase()));
    return (orgTags.data?.data ?? [])
      .filter((o) => !have.has(o.tag.toLowerCase()) && (!q || o.tag.toLowerCase().startsWith(q)))
      .slice(0, 8);
  }, [draft, current, orgTags.data]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'tags'] });
    qc.invalidateQueries({ queryKey: ['org-tags'] });
  }

  const add = useMutation({
    mutationFn: (tag: string) => inbox.addTag(conversationId, tag),
    onSuccess: () => { setDraft(''); setActive(-1); invalidate(); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.tags.addFailed'), t('inbox.tags.title'))
  });
  const remove = useMutation({
    mutationFn: (tag: string) => inbox.removeTag(conversationId, tag),
    onSuccess: invalidate,
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.tags.removeFailed'), t('inbox.tags.title'))
  });

  function commit(raw: string) {
    const v = raw.trim();
    if (!v) return;
    if (current.some((x) => x.toLowerCase() === v.toLowerCase())) { setDraft(''); setActive(-1); return; }
    add.mutate(v);
  }

  function close() { setAdding(false); setDraft(''); setActive(-1); }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        if (suggestions.length) { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
        return;
      case 'ArrowUp':
        if (suggestions.length) { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
        return;
      case 'Enter':
        e.preventDefault();
        commit(active >= 0 && suggestions[active] ? suggestions[active].tag : draft);
        return;
      case 'Tab':
        if (suggestions.length) { e.preventDefault(); commit((suggestions[Math.max(active, 0)] ?? suggestions[0]).tag); }
        return;
      case 'Escape':
        e.preventDefault();
        close();
        return;
      default:
        return;
    }
  }

  return (
    <Group gap={6} align="center" wrap="wrap" className="rr-inbox2__tags">
      {current.map((tag) => (
        <Pill
          key={tag}
          size="sm"
          withRemoveButton
          removeLabel={t('contacts.view.tags.remove', { tag })}
          onRemove={() => remove.mutate(tag)}
        >
          {tag}
        </Pill>
      ))}
      {adding ? (
        <div className="rr-inbox2__tag-editor">
          <TextInput
            size="xs"
            autoFocus
            placeholder={t('inbox.tags.placeholder')}
            aria-label={t('inbox.tags.add')}
            aria-autocomplete="list"
            aria-controls={suggestions.length ? listId : undefined}
            aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
            value={draft}
            onChange={(e) => { setDraft(e.currentTarget.value); setActive(-1); }}
            onKeyDown={onKeyDown}
            onBlur={() => setTimeout(close, 120)}
          />
          {suggestions.length > 0 && (
            <ul id={listId} role="listbox" className="rr-inbox2__suggest" aria-label={t('inbox.tags.suggestions')}>
              {suggestions.map((s, i) => (
                <li
                  key={s.tag}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? 'is-active' : undefined}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); commit(s.tag); }}
                >
                  <span>{s.tag}</span>
                  <span className="rr-inbox2__suggest-count">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <Button size="xs" variant="subtle" className={addClassName} onClick={() => setAdding(true)} loading={add.isPending}>
          + {t('inbox.tags.add')}
        </Button>
      )}
    </Group>
  );
}
